import { Controller } from "@hotwired/stimulus"
import { bindClickOutside } from "../utils/click_outside"
import { handleListKeydown } from "../utils/keyboard_nav"

// Search Autocomplete Controller
// In-memory filtering of roles for search - no server requests per keystroke
export default class extends Controller {
  static targets = ["input", "results", "rolesData"]
  static values = {
    minLength: { type: Number, default: 2 }
  }

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.filteredRoles = []
    this.roles = []

    // Load roles from script tag
    if (this.hasRolesDataTarget) {
      try {
        this.roles = JSON.parse(this.rolesDataTarget.textContent)
      } catch (e) {
        console.error("Failed to parse roles JSON:", e)
      }
    }

    // Close dropdown when clicking outside
    this.unbindClickOutside = bindClickOutside(this.element, () => this.closeResults())

    // Event delegation for result clicks
    if (this.hasResultsTarget) {
      this.resultsTarget.addEventListener("click", this.handleResultClick.bind(this))
      this.resultsTarget.addEventListener("mouseover", this.handleResultHover.bind(this))
    }
  }

  disconnect() {
    this.unbindClickOutside()
  }

  handleResultClick(event) {
    const item = event.target.closest(".autocomplete-item")
    if (item) {
      event.preventDefault()
      event.stopPropagation()
      const index = parseInt(item.dataset.index, 10)
      if (!isNaN(index) && this.filteredRoles[index]) {
        this.selectRole(this.filteredRoles[index])
      }
    }
  }

  handleResultHover(event) {
    const item = event.target.closest(".autocomplete-item")
    if (item) {
      const index = parseInt(item.dataset.index, 10)
      if (!isNaN(index) && index !== this.highlightedIndex) {
        this.highlightedIndex = index
        this.renderResults()
      }
    }
  }

  search(event) {
    const query = event.target.value.trim().toLowerCase()

    if (query.length < this.minLengthValue) {
      this.closeResults()
      return
    }

    this.filteredRoles = this.filterAndSort(query)
    this.renderResults()
    this.openResults()
  }

  filterAndSort(query) {
    const queryWords = query.split(/\s+/).filter(w => w.length > 0)

    const scored = this.roles
      .map(role => {
        const name = role.name.toLowerCase()
        const family = (role.family || "").toLowerCase()
        const field = (role.field || "").toLowerCase()
        const alternates = (role.alternates || []).map(a => a.toLowerCase())

        // Check if all query words match somewhere
        const allWordsMatch = queryWords.every(word => {
          return name.includes(word) ||
                 family.includes(word) ||
                 field.includes(word) ||
                 alternates.some(alt => alt.includes(word))
        })

        if (!allWordsMatch) {
          return { role, score: 0 }
        }

        // Score based on match quality
        let score = 0

        // Exact match on name
        if (name === query) {
          score = 1000
        }
        // Name starts with query
        else if (name.startsWith(query)) {
          score = 900
        }
        // First word matches start of name
        else if (name.startsWith(queryWords[0])) {
          score = 800
        }
        // Query is in name
        else if (name.includes(query)) {
          score = 700
        }
        // Words match in name
        else if (queryWords.some(w => name.includes(w))) {
          score = 500
        }
        // Matches in alternates
        else if (alternates.some(alt => queryWords.some(w => alt.includes(w)))) {
          score = 400
        }
        // Matches only in family/field
        else {
          score = 100
        }

        // Bonus for shorter names (more specific)
        score += Math.max(0, 50 - name.length)

        // Tiebreaker: search_count popularity
        score += (role.search_count || 0) / 10000

        return { role, score }
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map(item => item.role)

    return scored
  }

  renderResults() {
    if (!this.hasResultsTarget) return

    if (this.filteredRoles.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="autocomplete-empty px-3 py-2 text-muted text-gray-500">
          No matching roles found
        </div>
      `
      return
    }

    const html = this.filteredRoles.map((role, index) => {
      const isHighlighted = index === this.highlightedIndex

      return `
        <div class="autocomplete-item px-3 py-2 ${isHighlighted ? 'bg-light bg-gray-100' : ''}"
             data-index="${index}"
             style="cursor: pointer;">
          <div class="font-weight-medium font-medium">${this.escapeHtml(role.name)}</div>
          <div class="text-muted text-gray-500 small text-sm">${this.escapeHtml(role.field)} &middot; ${this.escapeHtml(role.family)}</div>
        </div>
      `
    }).join("")

    this.resultsTarget.innerHTML = html
  }

  openResults() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.remove("d-none", "hidden")
    this.isOpen = true
    this.highlightedIndex = -1
  }

  closeResults() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.add("d-none", "hidden")
    this.isOpen = false
    this.highlightedIndex = -1
  }

  selectRole(role) {
    this.closeResults()

    // Navigate directly to the role page
    if (role.slug) {
      window.location.href = `/salary-database/role/${role.slug}`
    } else {
      // Fallback: fill input and submit form
      if (this.hasInputTarget) {
        this.inputTarget.value = role.name
      }
      const form = this.element.closest("form")
      if (form) {
        form.submit()
      }
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    handleListKeydown(event, {
      highlightedIndex: this.highlightedIndex,
      itemCount: this.filteredRoles.length,
      onHighlight: (index) => { this.highlightedIndex = index; this.renderResults() },
      onSelect: (index) => this.selectRole(this.filteredRoles[index]),
      onClose: () => this.closeResults()
    })
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
